<script setup lang="ts">
import { onMounted } from 'vue';
import PageNormalTopHeroSection from '../components/PageNormalTopHeroSection.vue';
import PageNormalTopRankingSection from '../components/PageNormalTopRankingSection.vue';
import PageTopPolicySection from '../components/PageTopPolicySection.vue';
import { useRanking } from '../composables/useRanking';
import { useScrollMotion } from '../composables/useScrollMotion';
import VFooter from '~/components/VFooter.vue';
import { useErrorHandler } from '~/composables/useErrorHandler';
import { useLocalstorage } from '~/composables/useLocalstorage';
import { MODE } from '~/constants';
import { GTM_TOP_GAME_START } from '~/constants/gtm';

const { STORAGE_KEY, getStorage } = useLocalstorage();
const userId = computed(() => getStorage(STORAGE_KEY.USER_ID) as string);
const name = computed(
  () => (getStorage(STORAGE_KEY.USER_NAME) || '') as string,
);
const isAnswered = computed(
  () => getStorage(STORAGE_KEY.ANSWERED_QUESTIONNAIRE) === true,
);

const { topPageData, setData } = useRanking({
  mode: MODE.normal,
  userId: userId.value,
});

const { handleError } = useErrorHandler();
onMounted(() => {
  handleError(async () => {
    await setData();
  });
});

const { handleScroll, isFooterVisible } = useScrollMotion();
onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});
</script>

<template>
  <div class="min-w-[375px] pb-[98px]">
    <PageNormalTopHeroSection
      :current-question-number="topPageData.currentQuestionNumber"
    />
    <PageNormalTopRankingSection
      :score="topPageData.score"
      :best-score="topPageData.bestScore"
      :name="name"
      :ranking-top="topPageData.rankingTop"
      :ranking-user-id="topPageData.rankingUserId"
      :is-first-access-normal-mode="!isAnswered"
    />
    <Divider color="gray" />
    <PageTopPolicySection />
    <VFooter />
    <div
      :aria-hidden="!isFooterVisible"
      class="fixed-footer"
      :class="{
        visible: isFooterVisible,
        hidden: !isFooterVisible,
      }"
    >
      <NuxtLink :id="GTM_TOP_GAME_START" to="/question">
        <GameStartButton color="yellow" />
      </NuxtLink>
    </div>
  </div>
</template>

<style scoped>
.fixed-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  padding-inline: 16px;
  padding-top: 24px;
  padding-bottom: 16px;
  background: #e4dec0;
  transition:
    transform 0.1s ease,
    opacity 0.1s ease;
}

.fixed-footer::before {
  position: absolute;
  top: 0;
  width: 100%;
  height: 8px;
  content: '';
  background-image: repeating-linear-gradient(
    180deg,
    #ada67d,
    #ada67d 4px,
    #fffbe4 4px,
    #fffbe4 8px
  );
}

.fixed-footer.visible {
  transform: translateY(0);
}

.fixed-footer.hidden {
  transform: translateY(100%);
}
</style>
